import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    ShieldCheckIcon,
    ChatBubbleLeftRightIcon,
    BriefcaseIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    ArrowPathIcon,
    UserPlusIcon,
    XMarkIcon,
    MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import postUser from "../api/postUser";
import { getAdminUsers } from "../api/adminUsers";

const ROLES = [
    {
        value: "moderator",
        label: "Модератор",
        icon: ShieldCheckIcon,
        description: "Проверяет и одобряет объявления",
        tone: "text-orange-500 bg-orange-500/15",
        badge: "bg-orange-500/15 text-orange-500 border-orange-500/30",
    },
    {
        value: "operator",
        label: "Оператор",
        icon: ChatBubbleLeftRightIcon,
        description: "Отвечает пользователям в чатах поддержки",
        tone: "text-amber-500 bg-amber-500/15",
        badge: "bg-amber-500/15 text-amber-500 border-amber-500/30",
    },
    {
        value: "sales_manager",
        label: "Сату менеджері",
        icon: BriefcaseIcon,
        description: "Работает с клиентами и продажами",
        tone: "text-sky-500 bg-sky-500/15",
        badge: "bg-sky-500/15 text-sky-500 border-sky-500/30",
    },
];

const ROLE_BY_VALUE = Object.fromEntries(ROLES.map((r) => [r.value, r]));
const EMPTY_FORM = { email: "", name: "", role: "moderator" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputCls =
    "w-full rounded-lg border bg-bg-primary px-3 py-2.5 text-sm text-text-primary placeholder:text-text-muted " +
    "focus:outline-none focus:ring-2 transition-colors";

const humanizeError = (error) => {
    if (error?.status === 409) return "Пользователь с таким email уже существует.";
    if (error?.status === 403) return "Недостаточно прав: добавлять сотрудников может только администратор.";
    return error?.message || "Не удалось добавить пользователя. Попробуйте снова.";
};

function AddUser() {
    const [form, setForm] = useState(EMPTY_FORM);
    const [touched, setTouched] = useState({});
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null); // { type: 'success' | 'error', text, email? }
    const [staff, setStaff] = useState([]);
    const [staffState, setStaffState] = useState("loading");
    const [staffFilter, setStaffFilter] = useState("");
    const [staffQuery, setStaffQuery] = useState("");
    const [justAdded, setJustAdded] = useState(null);
    const emailRef = useRef(null);

    const errors = {
        email: !form.email.trim()
            ? "Укажите email"
            : !EMAIL_RE.test(form.email.trim())
            ? "Некорректный email"
            : null,
        name: !form.name.trim() ? "Укажите имя" : null,
    };
    const isValid = !errors.email && !errors.name;

    const loadStaff = useCallback(async () => {
        setStaffState("loading");
        try {
            const lists = await Promise.all(
                ROLES.map((r) =>
                    getAdminUsers({ user_type: r.value, limit: 100 })
                        .then((d) => d.items || [])
                        .catch(() => [])
                )
            );
            const all = lists
                .flat()
                .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
            setStaff(all);
            setStaffState("ready");
        } catch {
            setStaffState("error");
        }
    }, []);

    useEffect(() => {
        loadStaff();
    }, [loadStaff]);

    const handleChange = (e) => {
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
        if (result?.type === "error") setResult(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setTouched({ email: true, name: true });
        if (!isValid) return;

        setLoading(true);
        setResult(null);
        const payload = { email: form.email.trim().toLowerCase(), name: form.name.trim(), role: form.role };
        try {
            await postUser(payload.email, payload.name, payload.role);
            setResult({
                type: "success",
                text: `${payload.name} добавлен(а) как «${ROLE_BY_VALUE[payload.role].label}».`,
                email: payload.email,
            });
            setJustAdded(payload.email);
            setForm((f) => ({ ...EMPTY_FORM, role: f.role }));
            setTouched({});
            loadStaff();
            emailRef.current?.focus();
        } catch (error) {
            setResult({ type: "error", text: humanizeError(error) });
        } finally {
            setLoading(false);
        }
    };

    const filteredStaff = useMemo(() => {
        const q = staffQuery.trim().toLowerCase();
        return staff.filter(
            (u) =>
                (!staffFilter || u.user_type === staffFilter) &&
                (!q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q))
        );
    }, [staff, staffFilter, staffQuery]);

    const counts = useMemo(() => {
        const c = {};
        staff.forEach((u) => {
            c[u.user_type] = (c[u.user_type] || 0) + 1;
        });
        return c;
    }, [staff]);

    const fieldBorder = (name) =>
        touched[name] && errors[name]
            ? "border-rose-500/60 focus:ring-rose-500/30"
            : "border-border-primary focus:border-[#4F45E5] focus:ring-[#4F45E5]/30";

    return (
        <div className="space-y-4 text-left">
            <div>
                <h2 className="text-2xl font-bold text-text-primary">Добавить сотрудника</h2>
                <p className="mt-0.5 text-sm text-text-secondary">
                    Сотрудник входит в панель через Google-аккаунт с указанным email.
                </p>
            </div>

            <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-5">
                {/* Form */}
                <form
                    onSubmit={handleSubmit}
                    noValidate
                    className="rounded-block border border-border-primary bg-bg-card p-5 shadow-sm xl:col-span-2"
                >
                    <div className="space-y-4">
                        <label className="block">
                            <span className="mb-1.5 block text-sm font-medium text-text-primary">Email (Google-аккаунт)</span>
                            <input
                                ref={emailRef}
                                type="email"
                                name="email"
                                autoComplete="off"
                                autoFocus
                                placeholder="name@gmail.com"
                                value={form.email}
                                onChange={handleChange}
                                onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                                aria-invalid={Boolean(touched.email && errors.email)}
                                className={`${inputCls} ${fieldBorder("email")}`}
                            />
                            {touched.email && errors.email && (
                                <span className="mt-1 block text-xs text-rose-500">{errors.email}</span>
                            )}
                        </label>

                        <label className="block">
                            <span className="mb-1.5 block text-sm font-medium text-text-primary">Имя</span>
                            <input
                                type="text"
                                name="name"
                                autoComplete="off"
                                placeholder="Иван Иванов"
                                value={form.name}
                                onChange={handleChange}
                                onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                                aria-invalid={Boolean(touched.name && errors.name)}
                                className={`${inputCls} ${fieldBorder("name")}`}
                            />
                            {touched.name && errors.name && (
                                <span className="mt-1 block text-xs text-rose-500">{errors.name}</span>
                            )}
                        </label>

                        <fieldset>
                            <legend className="mb-1.5 text-sm font-medium text-text-primary">Роль</legend>
                            <div className="space-y-2" role="radiogroup">
                                {ROLES.map((r) => {
                                    const Icon = r.icon;
                                    const checked = form.role === r.value;
                                    return (
                                        <label
                                            key={r.value}
                                            className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors ${
                                                checked
                                                    ? "border-[#4F45E5] bg-[#4F45E5]/10 ring-1 ring-[#4F45E5]/40"
                                                    : "border-border-primary hover:bg-bg-tertiary"
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="role"
                                                value={r.value}
                                                checked={checked}
                                                onChange={handleChange}
                                                className="sr-only"
                                            />
                                            <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${r.tone}`}>
                                                <Icon className="h-5 w-5" />
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span className="block text-sm font-semibold text-text-primary">{r.label}</span>
                                                <span className="block text-xs text-text-secondary">{r.description}</span>
                                            </span>
                                            <span
                                                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                                                    checked ? "border-[#4F45E5]" : "border-border-primary"
                                                }`}
                                            >
                                                {checked && <span className="h-2 w-2 rounded-full bg-[#4F45E5]" />}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        </fieldset>
                    </div>

                    {result && (
                        <div
                            role={result.type === "error" ? "alert" : "status"}
                            className={`mt-4 flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm ${
                                result.type === "error"
                                    ? "border-rose-500/30 bg-rose-500/10 text-rose-500"
                                    : "border-emerald-500/30 bg-emerald-500/10 text-emerald-500"
                            }`}
                        >
                            {result.type === "error" ? (
                                <ExclamationTriangleIcon className="mt-px h-5 w-5 shrink-0" />
                            ) : (
                                <CheckCircleIcon className="mt-px h-5 w-5 shrink-0" />
                            )}
                            <span className="flex-1">{result.text}</span>
                            <button type="button" onClick={() => setResult(null)} aria-label="Закрыть" className="opacity-70 hover:opacity-100">
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-accent-grad px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                    >
                        {loading ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <UserPlusIcon className="h-4 w-4" />}
                        {loading ? "Добавление…" : "Добавить сотрудника"}
                    </button>
                </form>

                {/* Staff list */}
                <section className="overflow-hidden rounded-block border border-border-primary bg-bg-card shadow-sm xl:col-span-3">
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border-primary px-5 py-4">
                        <div>
                            <h3 className="text-base font-semibold text-text-primary">Сотрудники</h3>
                            <p className="mt-0.5 text-xs text-text-secondary">
                                {staffState === "ready" ? `Всего: ${staff.length}` : "Модераторы, операторы и менеджеры"}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={loadStaff}
                            disabled={staffState === "loading"}
                            aria-label="Обновить список"
                            title="Обновить"
                            className="rounded-lg border border-border-primary p-2 text-text-secondary hover:bg-bg-tertiary disabled:opacity-50"
                        >
                            <ArrowPathIcon className={`h-4 w-4 ${staffState === "loading" ? "animate-spin" : ""}`} />
                        </button>
                    </div>

                    <div className="flex flex-col gap-3 border-b border-border-primary px-5 py-3 sm:flex-row sm:items-center">
                        <div className="flex flex-wrap gap-1.5">
                            {[{ value: "", label: "Все" }, ...ROLES].map((r) => {
                                const active = staffFilter === r.value;
                                const count = r.value ? counts[r.value] || 0 : staff.length;
                                return (
                                    <button
                                        key={r.value || "all"}
                                        type="button"
                                        onClick={() => setStaffFilter(r.value)}
                                        className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                                            active
                                                ? "border-[#4F45E5]/40 bg-[#4F45E5]/15 text-text-primary"
                                                : "border-border-primary text-text-secondary hover:bg-bg-tertiary"
                                        }`}
                                    >
                                        {r.label}
                                        {staffState === "ready" && <span className="ml-1.5 text-text-muted">{count}</span>}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="relative sm:ml-auto sm:w-56">
                            <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
                            <input
                                type="text"
                                value={staffQuery}
                                onChange={(e) => setStaffQuery(e.target.value)}
                                placeholder="Имя или email"
                                aria-label="Поиск сотрудника"
                                className="w-full rounded-lg border border-border-primary bg-bg-primary py-1.5 pl-8 pr-3 text-sm text-text-primary placeholder:text-text-muted focus:border-[#4F45E5] focus:outline-none focus:ring-2 focus:ring-[#4F45E5]/30"
                            />
                        </div>
                    </div>

                    <ul className="max-h-[520px] divide-y divide-border-primary overflow-y-auto">
                        {staffState === "loading" &&
                            Array.from({ length: 5 }).map((_, i) => (
                                <li key={i} className="flex animate-pulse items-center gap-3 px-5 py-3">
                                    <div className="h-9 w-9 rounded-full bg-bg-tertiary" />
                                    <div className="flex-1 space-y-2">
                                        <div className="h-3 w-1/3 rounded bg-bg-tertiary" />
                                        <div className="h-3 w-1/2 rounded bg-bg-tertiary" />
                                    </div>
                                </li>
                            ))}
                        {staffState === "error" && (
                            <li className="px-5 py-10 text-center text-sm text-text-muted">Не удалось загрузить список</li>
                        )}
                        {staffState === "ready" && filteredStaff.length === 0 && (
                            <li className="px-5 py-10 text-center text-sm text-text-muted">Никого не найдено</li>
                        )}
                        {staffState === "ready" &&
                            filteredStaff.map((u) => {
                                const role = ROLE_BY_VALUE[u.user_type];
                                const isNew = justAdded && u.email?.toLowerCase() === justAdded;
                                return (
                                    <li
                                        key={u.id}
                                        className={`flex items-center gap-3 px-5 py-3 ${isNew ? "bg-emerald-500/10" : ""}`}
                                    >
                                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-grad text-sm font-semibold text-white">
                                            {(u.name || u.email || "?")[0].toUpperCase()}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className="truncate text-sm font-medium text-text-primary">
                                                    {u.name || <span className="italic text-text-muted">Без имени</span>}
                                                </span>
                                                {isNew && (
                                                    <span className="rounded bg-emerald-500/20 px-1.5 py-px text-[10px] font-semibold uppercase text-emerald-500">
                                                        новый
                                                    </span>
                                                )}
                                            </div>
                                            <div className="truncate text-xs text-text-secondary">{u.email || "—"}</div>
                                        </div>
                                        {role && (
                                            <span className={`hidden shrink-0 rounded-md border px-2 py-0.5 text-xs font-medium sm:inline ${role.badge}`}>
                                                {role.label}
                                            </span>
                                        )}
                                        <span className="hidden w-20 shrink-0 text-right text-xs text-text-muted md:block">
                                            {u.created_at ? new Date(u.created_at).toLocaleDateString("ru-RU") : ""}
                                        </span>
                                    </li>
                                );
                            })}
                    </ul>
                </section>
            </div>
        </div>
    );
}

export default AddUser;
